/**
 * Камера.
 *
 * Веб-реализация — `<input type="file" capture="environment">`,
 * поэтому здесь только подготовка снимка; сам ввод остаётся
 * в компоненте. В нативной сборке модуль заменяется на плагин камеры.
 */

const MAX_EDGE = 1600
const QUALITY = 0.8
/** Ниже этого порога пережимать нечего. */
const MIN_COMPRESS_BYTES = 300 * 1024

/**
 * Пресеты сжатия.
 *
 * Фото поломки переживёт сильное сжатие; накладная и ТОРГ-2 — нет:
 * нечитаемая цифра в документе обесценивает снимок целиком, поэтому
 * документам — больше пикселей и выше качество.
 */
export const PRESET_PHOTO = { maxEdge: MAX_EDGE, quality: QUALITY }
export const PRESET_DOCUMENT = { maxEdge: 2000, quality: 0.85 }

/**
 * Сжать снимок.
 *
 * Обязательный шаг, а не оптимизация: несжатый кадр 12 Мп занимает
 * 3–5 МБ, шесть таких не проходят лимит запроса (20 МБ) и не влезают
 * в разумную квоту IndexedDB.
 *
 * **Ориентация применяется при декодировании.** Canvas не сохраняет
 * EXIF, поэтому после пережатия серверный exif_transpose не находит
 * тега и ничего не поворачивает — снимок, снятый вертикально, ляжет
 * боком. imageOrientation: 'from-image' разворачивает картинку
 * до отрисовки, и метаданные становятся не нужны.
 *
 * @returns {Promise<Blob>} сжатый снимок либо исходный файл, если
 *   сжать не удалось: потерять фото хуже, чем отправить большое.
 */
export async function compress(file, maxEdge = MAX_EDGE, quality = QUALITY) {
  if (!file || !globalThis.createImageBitmap) return file

  let bitmap
  try {
    bitmap = await decode(file)
  } catch {
    return file
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))

    // Мелкий снимок не уменьшаем, но пережимаем, если он тяжёлый:
    // 1000 пикселей при высоком качестве — это всё ещё мегабайты.
    if (scale === 1 && file.size <= MIN_COMPRESS_BYTES) return file

    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const blob = await draw(bitmap, width, height, quality)
    if (!blob) return file

    // Если «сжатие» увеличило файл — отдаём исходный. Так бывает
    // с уже оптимизированными или очень мелкими картинками.
    return blob.size < file.size ? blob : file
  } catch {
    return file
  } finally {
    bitmap.close?.()
  }
}

/**
 * Декодировать с учётом ориентации.
 *
 * Второй аргумент createImageBitmap поддерживается не везде;
 * там, где нет, откатываемся к обычному декодированию — снимок
 * может лечь боком, но это лучше, чем не отправить его вовсе.
 */
async function decode(file) {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return await createImageBitmap(file)
  }
}

/**
 * Отрисовать и получить Blob.
 *
 * OffscreenCanvas появился в Safari только в 16.4, а водители
 * с айфонами постарше — обычное дело. Без запасного пути они
 * отправляли бы несжатые снимки и упирались в лимит запроса.
 */
async function draw(bitmap, width, height, quality) {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height)
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)
    return canvas.convertToBlob({ type: 'image/jpeg', quality })
  }

  const canvas = globalThis.document?.createElement?.('canvas')
  if (!canvas) return null

  canvas.width = width
  canvas.height = height
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

export const MAX_EDGE_PX = MAX_EDGE
export const JPEG_QUALITY = QUALITY
